import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  BgColorsOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ExpandOutlined,
  FileImageOutlined,
  LoadingOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  SkinOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { ProductService } from '../services/productService';
import {
  AiModel,
  BrandCustomModel,
  OnboardingService,
} from '../services/onboardingService';
import { useAuth } from '../contexts/AuthContext';
import { getActiveBrandId } from '../config/api';
import {
  ProductToImageBackgroundColor,
  ProductToImageCreateResult,
  ProductToImageEnvironment,
  ProductToImageGeneratedImage,
  ProductToImageHistoryItem,
  ProductToImageMannequinSource,
  ProductToImageMode,
  ProductToImagePose,
  ProductToImageService,
  ProductUploadValidationResult,
} from '../services/productToImageService';
import {
  CreateImageToVideoJobInput,
  ProductMediaService,
  VideoJobResult,
} from '../services/productMediaService';
import './ProductStudioPage.css';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;
const { TextArea } = Input;

interface StudioFormValues {
  name: string;
  category: string;
  description?: string;
}

interface VideoFormValues {
  prompt?: string;
  negativePrompt?: string;
}

interface PreviewModalState {
  open: boolean;
  url?: string;
  title?: string;
}

type StudioTabKey = 'image_to_product' | 'image_to_video';

type MotionPresetKey = 'runway_walk' | 'subtle_editorial' | 'camera_push_in' | 'fabric_movement';

interface MotionPreset {
  key: MotionPresetKey;
  label: string;
  prompt: string;
  hint: string;
}

const MOTION_PRESETS: MotionPreset[] = [
  {
    key: 'runway_walk',
    label: 'Runway Walk',
    prompt: 'Model takes a smooth runway walk forward with confident fashion pace, preserving outfit details.',
    hint: 'Catwalk hissi, daha belirgin hareket',
  },
  {
    key: 'subtle_editorial',
    label: 'Subtle Editorial',
    prompt: 'Subtle editorial movement with gentle breathing motion and minimal camera drift.',
    hint: 'Premium ama sakin editorial hareket',
  },
  {
    key: 'camera_push_in',
    label: 'Camera Push-In',
    prompt: 'Slow camera push-in on the outfit with slight natural body motion and stable framing.',
    hint: 'Kamera odakli zoom-in etkisi',
  },
  {
    key: 'fabric_movement',
    label: 'Fabric Movement',
    prompt: 'Gentle wind causes natural fabric movement while the model remains mostly steady.',
    hint: 'Kumas davranisi odakli',
  },
];

const DEFAULT_CATEGORIES = [
  'T-Shirt',
  'Gomlek',
  'Sweatshirt',
  'Kazak',
  'Ceket',
  'Kaban',
  'Pantolon',
  'Jean',
  'Etek',
  'Sort',
  'Elbise',
  'Tulum',
];

const BACKGROUND_OPTIONS: Array<{
  value: ProductToImageBackgroundColor;
  label: string;
}> = [
  { value: 'pure_white', label: 'Pure White' },
  { value: 'soft_ivory', label: 'Soft Ivory' },
  { value: 'warm_sand', label: 'Warm Sand' },
  { value: 'cool_gray', label: 'Cool Gray' },
  { value: 'deep_charcoal', label: 'Deep Charcoal' },
];

const ENVIRONMENT_OPTIONS: Array<{
  value: ProductToImageEnvironment;
  label: string;
}> = [
  { value: 'studio', label: 'Studio Backdrop' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'street', label: 'Street' },
  { value: 'showroom', label: 'Showroom' },
  { value: 'loft', label: 'Loft' },
];

const GHOST_POSE_OPTIONS: Array<{ value: ProductToImagePose; label: string }> = [
  { value: 'front_packshot', label: 'Front Hero' },
  { value: 'three_quarter_packshot', label: 'Soft Angle' },
  { value: 'detail_packshot', label: 'Detail Focus' },
];

const MODEL_POSE_OPTIONS: Array<{ value: ProductToImagePose; label: string }> = [
  { value: 'front_catalog', label: 'Front Catalog' },
  { value: 'three_quarter_catalog', label: 'Three Quarter' },
  { value: 'walk_catalog', label: 'Soft Walk' },
  { value: 'editorial_catalog', label: 'Elevated' },
];

const OUTPUT_OPTIONS = [1, 2, 3, 4];

const ProductStudioPage: React.FC = () => {
  const { user } = useAuth();
  const [studioForm] = Form.useForm<StudioFormValues>();
  const [videoForm] = Form.useForm<VideoFormValues>();

  const [activeTab, setActiveTab] = useState<StudioTabKey>('image_to_product');

  const [uploadedProductImage, setUploadedProductImage] = useState<string | null>(null);
  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [studioMode, setStudioMode] = useState<ProductToImageMode>('ghost');
  const [backgroundColor, setBackgroundColor] = useState<ProductToImageBackgroundColor>('pure_white');
  const [environment, setEnvironment] = useState<ProductToImageEnvironment>('studio');
  const [pose, setPose] = useState<ProductToImagePose>('front_packshot');
  const [outputCount, setOutputCount] = useState(2);

  const [aiModels, setAiModels] = useState<AiModel[]>([]);
  const [customModels, setCustomModels] = useState<BrandCustomModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [selectedAiModel, setSelectedAiModel] = useState<string | null>(null);
  const [selectedModelType, setSelectedModelType] = useState<ProductToImageMannequinSource | null>(null);

  const [validationResult, setValidationResult] = useState<ProductUploadValidationResult | null>(null);
  const [result, setResult] = useState<ProductToImageCreateResult | null>(null);
  const [historyItems, setHistoryItems] = useState<ProductToImageHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [studioSubmitting, setStudioSubmitting] = useState(false);
  const [studioStep, setStudioStep] = useState(0);

  const [videoUploadImage, setVideoUploadImage] = useState<string | null>(null);
  const [videoSourceUrl, setVideoSourceUrl] = useState<string | null>(null);
  const [motionPreset, setMotionPreset] = useState<MotionPresetKey>('runway_walk');
  const [videoJobId, setVideoJobId] = useState<string | null>(null);
  const [videoJobState, setVideoJobState] = useState<VideoJobResult | null>(null);
  const [videoSubmitting, setVideoSubmitting] = useState(false);
  const [lastVideoPayload, setLastVideoPayload] = useState<CreateImageToVideoJobInput | null>(null);

  const [previewModal, setPreviewModal] = useState<PreviewModalState>({ open: false });
  const [downloadingPreview, setDownloadingPreview] = useState(false);

  const activeBrandId = getActiveBrandId(user);
  const categoryOptions = useMemo(
    () => Array.from(new Set([...DEFAULT_CATEGORIES, ...existingCategories])),
    [existingCategories]
  );
  const poseOptions = studioMode === 'ghost' ? GHOST_POSE_OPTIONS : MODEL_POSE_OPTIONS;

  const selectedPreset = MOTION_PRESETS.find((preset) => preset.key === motionPreset) || MOTION_PRESETS[0];

  const productResultImages = useMemo(() => {
    if (!result) {
      return [] as ProductToImageGeneratedImage[];
    }
    if (result.generatedImages?.length) {
      return result.generatedImages;
    }
    if (result.generatedImageUrls?.length) {
      return result.generatedImageUrls.map((url, index) => ({
        url,
        shotKey: `generated-${index + 1}`,
        shotLabel: `Cikti ${index + 1}`,
      }));
    }
    if (result.generatedImageUrl) {
      return [{ url: result.generatedImageUrl, shotKey: 'generated-1', shotLabel: 'Hero' }];
    }
    return [];
  }, [result]);

  const sourceVideoImage = videoUploadImage || videoSourceUrl;

  const resetStudioOutput = useCallback(() => {
    setValidationResult(null);
    setResult(null);
    setStudioStep(0);
  }, []);

  const loadHistory = useCallback(async () => {
    if (!activeBrandId) {
      return;
    }
    try {
      setHistoryLoading(true);
      const items = await ProductToImageService.getHistory(40);
      setHistoryItems(items);
    } catch (error) {
      console.error('Failed to load studio history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [activeBrandId]);

  const loadAiModels = useCallback(async () => {
    if (!activeBrandId || studioMode !== 'ai_model') {
      return;
    }
    try {
      setLoadingModels(true);
      const [systemModels, customModelData] = await Promise.all([
        OnboardingService.getAiModels(),
        OnboardingService.getBrandCustomModels(activeBrandId),
      ]);
      setAiModels(systemModels);
      setCustomModels(customModelData.models.filter((model) => model.isActive !== false));
    } catch (error) {
      console.error('Failed to load AI models:', error);
      message.error('AI modeller yuklenemedi');
    } finally {
      setLoadingModels(false);
    }
  }, [activeBrandId, studioMode]);

  useEffect(() => {
    const loadCategories = async () => {
      if (!activeBrandId) {
        return;
      }
      try {
        setLoadingCategories(true);
        const categories = await ProductService.getCategories(activeBrandId);
        setExistingCategories(categories.map((item) => item.name));
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, [activeBrandId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (studioMode === 'ai_model') {
      loadAiModels();
    }
  }, [studioMode, loadAiModels]);

  useEffect(() => {
    if (!studioForm.getFieldValue('category') && categoryOptions[0]) {
      studioForm.setFieldsValue({ category: categoryOptions[0] });
    }
  }, [categoryOptions, studioForm]);

  const openImagePreview = (url: string, title: string) => {
    setPreviewModal({ open: true, url, title });
  };

  const handleDownloadPreview = async () => {
    if (!previewModal.url) {
      return;
    }
    try {
      setDownloadingPreview(true);
      const response = await fetch(previewModal.url);
      if (!response.ok) {
        throw new Error('Gorsel alinamadi');
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'product-studio-media.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      window.open(previewModal.url, '_blank', 'noopener,noreferrer');
      message.info('Gorsel yeni sekmede acildi.');
    } finally {
      setDownloadingPreview(false);
    }
  };

  const handleProductImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedProductImage(event.target?.result as string);
      resetStudioOutput();
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleVideoImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      setVideoUploadImage(event.target?.result as string);
      setVideoSourceUrl(null);
      setVideoJobState(null);
      setVideoJobId(null);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleStudioSubmit = async (values: StudioFormValues) => {
    if (!uploadedProductImage) {
      message.error('Lutfen urun gorseli yukleyin');
      return;
    }
    if (studioMode === 'ai_model' && (!selectedAiModel || !selectedModelType)) {
      message.error('AI model secimi gerekli');
      return;
    }

    try {
      setStudioSubmitting(true);
      setStudioStep(0);
      setResult(null);

      const validation = await ProductToImageService.validateUpload(uploadedProductImage, values.category);
      setValidationResult(validation);
      if (!validation.valid) {
        message.error(validation.reason || 'Gorsel uygun degil');
        return;
      }
      setStudioStep(1);

      const generationResult = await ProductToImageService.createFromImage({
        name: values.name,
        category: values.category,
        description: values.description,
        image: uploadedProductImage,
        mode: studioMode,
        backgroundColor,
        environment: studioMode === 'ai_model' ? environment : undefined,
        pose,
        outputCount,
        mannequinSource: selectedModelType || undefined,
        mannequinId: selectedAiModel || undefined,
      });

      setResult(generationResult);
      setStudioStep(2);
      await loadHistory();

      if (generationResult.generationStatus === 'completed') {
        message.success('Studio gorselleri hazir');
      } else {
        message.warning('Urun olusturuldu ama gorsel uretimi tamamlanmadi');
      }
    } catch (error: any) {
      console.error('Studio generation failed:', error);
      message.error(error.message || 'Studio olusturma sirasinda hata olustu');
    } finally {
      setStudioSubmitting(false);
    }
  };

  const runVideoJob = async (payload: CreateImageToVideoJobInput) => {
    setVideoSubmitting(true);
    setVideoJobState({ status: 'queued' });
    setLastVideoPayload(payload);

    try {
      const createResult = await ProductMediaService.createImageToVideoJob(payload);
      setVideoJobId(createResult.jobId);
      const finalState = await ProductMediaService.pollVideoJob(createResult.jobId, {
        intervalMs: 3000,
        timeoutMs: 10 * 60 * 1000,
        onTick: (state) => setVideoJobState(state),
      });
      setVideoJobState(finalState);
      if (finalState.status === 'completed') {
        message.success('Video hazir');
      } else {
        message.error(finalState.error || 'Video olusturma basarisiz');
      }
    } catch (error: any) {
      setVideoJobState({
        status: 'failed',
        error: error.message || 'Video olusturma basarisiz',
      });
      message.error(error.message || 'Video olusturma basarisiz');
    } finally {
      setVideoSubmitting(false);
    }
  };

  const buildVideoPayload = (input: { image?: string; imageUrl?: string; formValues?: VideoFormValues }): CreateImageToVideoJobInput => {
    const customPrompt = input.formValues?.prompt?.trim();
    const combinedPrompt = customPrompt
      ? `${selectedPreset.prompt} ${customPrompt}`
      : selectedPreset.prompt;

    return {
      image: input.image,
      imageUrl: input.imageUrl,
      prompt: combinedPrompt,
      negativePrompt: input.formValues?.negativePrompt?.trim() || undefined,
      qualityPreset: 'balanced',
      width: 480,
      height: 832,
    };
  };

  const handleVideoSubmit = async (values: VideoFormValues) => {
    if (!sourceVideoImage) {
      message.error('Lutfen once bir kombin/outfit gorseli ekleyin');
      return;
    }
    const payload = buildVideoPayload({
      image: videoUploadImage || undefined,
      imageUrl: videoUploadImage ? undefined : videoSourceUrl || undefined,
      formValues: values,
    });
    await runVideoJob(payload);
  };

  const handleConvertImageToVideo = async (imageUrl: string) => {
    setActiveTab('image_to_video');
    setVideoUploadImage(null);
    setVideoSourceUrl(imageUrl);
    videoForm.resetFields();

    const payload = buildVideoPayload({ imageUrl });
    await runVideoJob(payload);
  };

  const renderImageCard = (
    image: ProductToImageGeneratedImage,
    titlePrefix: string
  ) => (
    <div key={`${image.shotKey}-${image.url}`} className="studio-gallery-item">
      <button
        type="button"
        className="studio-clickable-image"
        onClick={() => openImagePreview(image.url, `${titlePrefix} - ${image.shotLabel}`)}
      >
        <img src={image.url} alt={image.shotLabel} className="studio-gallery-image" />
        <span className="studio-image-overlay">
          <ExpandOutlined />
          <span>Buyut</span>
        </span>
      </button>
      <Space wrap>
        <Tag>{image.shotLabel}</Tag>
        <Button size="small" icon={<DownloadOutlined />} onClick={() => window.open(image.url, '_blank', 'noopener,noreferrer')}>
          Indir
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<VideoCameraOutlined />}
          onClick={() => handleConvertImageToVideo(image.url)}
          loading={videoSubmitting}
        >
          Videoya donustur
        </Button>
      </Space>
    </div>
  );

  const renderValidationState = () => {
    if (!validationResult) {
      return (
        <Alert
          type="info"
          showIcon
          message="Validasyon bekleniyor"
          description="Uretim baslayinca gorsel kalite ve urun algisi kontrol edilir."
        />
      );
    }
    if (!validationResult.valid) {
      return (
        <Alert
          type="error"
          showIcon
          message={validationResult.reason || 'Gorsel uygun degil'}
          description={validationResult.summary || 'Tek urun ve net gorsel ile tekrar deneyin.'}
        />
      );
    }
    return (
      <Alert
        type="success"
        showIcon
        message="Gorsel uygun bulundu"
        description={validationResult.summary || 'Studio olusturma icin uygun gorsel.'}
      />
    );
  };

  const renderVideoStatus = () => {
    const status = videoJobState?.status;
    if (!status) {
      return (
        <Alert
          type="info"
          showIcon
          message="Video bekleniyor"
          description="Kaynak gorsel ve hareket preset secip videoyu baslatin."
        />
      );
    }
    if (status === 'failed') {
      return (
        <Alert
          type="error"
          showIcon
          message="Video olusturma basarisiz"
          description={videoJobState?.error || 'Istek basarisiz oldu'}
        />
      );
    }
    if (status === 'completed') {
      return (
        <Alert
          type="success"
          showIcon
          message="Video hazir"
          description={`Job: ${videoJobId || '-'}`}
        />
      );
    }
    return (
      <Alert
        type="warning"
        showIcon
        message="Video uretiliyor"
        description={`Durum: ${status}`}
      />
    );
  };

  const productHistoryImages = (item: ProductToImageHistoryItem): ProductToImageGeneratedImage[] => {
    if (item.generatedImages?.length) {
      return item.generatedImages;
    }
    if (item.generatedImageUrls?.length) {
      return item.generatedImageUrls.map((url, index) => ({
        url,
        shotKey: `history-${item.id}-${index}`,
        shotLabel: `Cikti ${index + 1}`,
      }));
    }
    if (item.generatedImageUrl) {
      return [{ url: item.generatedImageUrl, shotKey: `history-${item.id}-single`, shotLabel: 'Hero' }];
    }
    return [];
  };

  return (
    <div className="product-studio-page">
      <Card className="product-studio-hero" bordered={false}>
        <Space direction="vertical" size={10}>
          <Tag className="product-studio-badge" icon={<PictureOutlined />}>Product Studio</Tag>
          <Title level={2} style={{ margin: 0, color: '#fff' }}>
            Image to Product ve Image to Video tek ekranda
          </Title>
          <Paragraph style={{ marginBottom: 0, color: 'rgba(255,255,255,0.88)' }}>
            Urun gorseli ile katalog seti olusturun, isterseniz cikan her gorseli tek tikla videoya donusturun.
          </Paragraph>
        </Space>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as StudioTabKey)}
        items={[
          {
            key: 'image_to_product',
            label: 'Image to Product',
            children: (
              <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <div className="product-studio-layout">
                  <Card title="Kaynak ve Urun Bilgisi" className="studio-panel-left">
                    <Form layout="vertical" form={studioForm} onFinish={handleStudioSubmit}>
                      <Form.Item label="Urun Gorseli" required>
                        <Dragger
                          accept="image/*"
                          maxCount={1}
                          showUploadList={false}
                          beforeUpload={handleProductImageUpload}
                          disabled={studioSubmitting}
                        >
                          {uploadedProductImage ? (
                            <div className="studio-upload-preview">
                              <img src={uploadedProductImage} alt="Urun" />
                              <Text type="secondary">Degistirmek icin tiklayin</Text>
                            </div>
                          ) : (
                            <div className="studio-upload-empty">
                              <FileImageOutlined />
                              <Text strong>Urun gorselini yukleyin</Text>
                              <Text type="secondary">Tek urun, net kadraj, temiz fon</Text>
                            </div>
                          )}
                        </Dragger>
                      </Form.Item>
                      <Form.Item name="name" label="Urun Ismi" rules={[{ required: true, message: 'Urun ismi gerekli' }]}>
                        <Input placeholder="Orn. Satin Bluz" />
                      </Form.Item>
                      <Form.Item name="category" label="Kategori" rules={[{ required: true, message: 'Kategori secin' }]}>
                        <Select
                          loading={loadingCategories}
                          options={categoryOptions.map((item) => ({ value: item, label: item }))}
                          showSearch
                          optionFilterProp="label"
                        />
                      </Form.Item>
                      <Form.Item name="description" label="Kisa Not (Opsiyonel)">
                        <TextArea rows={3} placeholder="Kumas, koleksiyon veya styling notu..." />
                      </Form.Item>
                    </Form>
                  </Card>

                  <Card title="Canli Canvas" className="studio-panel-center">
                    {!uploadedProductImage ? (
                      <Empty description="Preview icin once gorsel yukleyin" />
                    ) : (
                      <Space direction="vertical" size={14} style={{ width: '100%' }}>
                        <div className="studio-canvas-ref">
                          <Text strong>Referans</Text>
                          <button
                            type="button"
                            className="studio-clickable-image"
                            onClick={() => openImagePreview(uploadedProductImage, 'Referans gorsel')}
                          >
                            <img src={uploadedProductImage} alt="Referans" className="studio-reference-image" />
                          </button>
                        </div>
                        <div>
                          <Text strong>Sonuclar</Text>
                          {productResultImages.length ? (
                            <div className="studio-gallery">{productResultImages.map((image) => renderImageCard(image, 'Studio'))}</div>
                          ) : (
                            <div className="studio-empty-result">Uretim sonrasi gorseller burada gorunecek</div>
                          )}
                        </div>
                      </Space>
                    )}
                  </Card>

                  <Card title="Creative Direction" className="studio-panel-right">
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      <div>
                        <Text strong>Mode</Text>
                        <div className="studio-segment-grid">
                          <button
                            type="button"
                            className={`studio-segment-card ${studioMode === 'ghost' ? 'selected' : ''}`}
                            onClick={() => {
                              setStudioMode('ghost');
                              setPose('front_packshot');
                              resetStudioOutput();
                            }}
                          >
                            <SkinOutlined />
                            <span>Ghost</span>
                          </button>
                          <button
                            type="button"
                            className={`studio-segment-card ${studioMode === 'ai_model' ? 'selected' : ''}`}
                            onClick={() => {
                              setStudioMode('ai_model');
                              setPose('front_catalog');
                              resetStudioOutput();
                            }}
                          >
                            <RobotOutlined />
                            <span>AI Model</span>
                          </button>
                        </div>
                      </div>

                      <div>
                        <Text strong><BgColorsOutlined /> Arka Plan</Text>
                        <div className="studio-chip-grid">
                          {BACKGROUND_OPTIONS.map((option) => (
                            <button
                              type="button"
                              key={option.value}
                              className={`studio-chip ${backgroundColor === option.value ? 'selected' : ''}`}
                              onClick={() => {
                                setBackgroundColor(option.value);
                                resetStudioOutput();
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Text strong><EnvironmentOutlined /> Ortam</Text>
                        <div className="studio-chip-grid">
                          {ENVIRONMENT_OPTIONS.map((option) => (
                            <button
                              type="button"
                              key={option.value}
                              className={`studio-chip ${environment === option.value ? 'selected' : ''}`}
                              onClick={() => {
                                setEnvironment(option.value);
                                resetStudioOutput();
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Text strong><CameraOutlined /> Poz</Text>
                        <div className="studio-chip-grid">
                          {poseOptions.map((option) => (
                            <button
                              type="button"
                              key={option.value}
                              className={`studio-chip ${pose === option.value ? 'selected' : ''}`}
                              onClick={() => {
                                setPose(option.value);
                                resetStudioOutput();
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Text strong>Cikti Adedi</Text>
                        <Space wrap>
                          {OUTPUT_OPTIONS.map((count) => (
                            <Button
                              key={count}
                              type={outputCount === count ? 'primary' : 'default'}
                              onClick={() => {
                                setOutputCount(count);
                                resetStudioOutput();
                              }}
                            >
                              {count}
                            </Button>
                          ))}
                        </Space>
                      </div>

                      {studioMode === 'ai_model' && (
                        <div>
                          <Text strong><UserOutlined /> Model Secimi</Text>
                          {loadingModels ? (
                            <div className="studio-loading-box"><Spin /></div>
                          ) : (
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                              {customModels.map((model) => (
                                <Col span={12} key={`custom-${model.id}`}>
                                  <Button
                                    block
                                    type={selectedAiModel === model.id && selectedModelType === 'custom' ? 'primary' : 'default'}
                                    onClick={() => {
                                      setSelectedAiModel(model.id);
                                      setSelectedModelType('custom');
                                      resetStudioOutput();
                                    }}
                                  >
                                    {model.name}
                                  </Button>
                                </Col>
                              ))}
                              {aiModels.map((model) => (
                                <Col span={12} key={`system-${model.id}`}>
                                  <Button
                                    block
                                    type={selectedAiModel === model.id && selectedModelType === 'system' ? 'primary' : 'default'}
                                    onClick={() => {
                                      setSelectedAiModel(model.id);
                                      setSelectedModelType('system');
                                      resetStudioOutput();
                                    }}
                                  >
                                    {model.name}
                                  </Button>
                                </Col>
                              ))}
                            </Row>
                          )}
                        </div>
                      )}

                      <Card size="small">
                        <Steps
                          size="small"
                          direction="vertical"
                          current={studioStep}
                          items={[
                            { title: 'Validasyon' },
                            { title: 'Urun olusturma' },
                            { title: 'Gorsel uretimi' },
                          ]}
                        />
                      </Card>

                      {renderValidationState()}

                      <Button
                        type="primary"
                        size="large"
                        icon={studioSubmitting ? <LoadingOutlined /> : <CheckCircleOutlined />}
                        loading={studioSubmitting}
                        onClick={() => studioForm.submit()}
                        disabled={studioMode === 'ai_model' && (!selectedAiModel || !selectedModelType)}
                      >
                        Gorselleri Uret
                      </Button>
                    </Space>
                  </Card>
                </div>

                <Card
                  title={`Gecmis Uretimler (${historyItems.length})`}
                  extra={<Button onClick={() => loadHistory()} loading={historyLoading}>Yenile</Button>}
                >
                  {historyLoading ? (
                    <div className="studio-loading-box"><Spin /></div>
                  ) : historyItems.length === 0 ? (
                    <Empty description="Henuz uretim yok" />
                  ) : (
                    <div className="studio-history-grid">
                      {historyItems.map((item) => {
                        const images = productHistoryImages(item);
                        return (
                          <Card
                            key={item.id}
                            size="small"
                            title={item.title}
                            extra={<Tag>{item.generationStatus}</Tag>}
                          >
                            {images.length ? (
                              <div className="studio-gallery">
                                {images.map((image) => renderImageCard(image, item.title))}
                              </div>
                            ) : (
                              <Text type="secondary">Sonuc gorseli yok</Text>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </Space>
            ),
          },
          {
            key: 'image_to_video',
            label: 'Image to Video',
            children: (
              <div className="product-studio-layout">
                <Card title="Kaynak Gorsel" className="studio-panel-left">
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Dragger
                      accept="image/*"
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={handleVideoImageUpload}
                      disabled={videoSubmitting}
                    >
                      {sourceVideoImage ? (
                        <div className="studio-upload-preview">
                          <img src={sourceVideoImage} alt="Video source" />
                          <Text type="secondary">{videoSourceUrl ? 'Studio sonucundan secildi' : 'Yuklenen gorsel'}</Text>
                        </div>
                      ) : (
                        <div className="studio-upload-empty">
                          <FileImageOutlined />
                          <Text strong>Kombin/Outfit gorseli ekleyin</Text>
                        </div>
                      )}
                    </Dragger>
                    <Text type="secondary">
                      Gorsel ilk frame olarak korunur. Prompt sadece hareket ve kamera niyeti icin kullanilir.
                    </Text>
                  </Space>
                </Card>

                <Card title="Video Preview" className="studio-panel-center">
                  {videoJobState?.status === 'completed' && videoJobState.videoUrl ? (
                    <Space direction="vertical" style={{ width: '100%' }} size={14}>
                      <video src={videoJobState.videoUrl} controls className="studio-video-player" />
                      <Space wrap>
                        <Button
                          icon={<PlayCircleOutlined />}
                          onClick={() => window.open(videoJobState.videoUrl, '_blank', 'noopener,noreferrer')}
                        >
                          Yeni sekmede ac
                        </Button>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => window.open(videoJobState.videoUrl, '_blank', 'noopener,noreferrer')}
                        >
                          Indir
                        </Button>
                        <Button
                          onClick={() => {
                            if (lastVideoPayload) {
                              runVideoJob(lastVideoPayload);
                            }
                          }}
                        >
                          Tekrar dene
                        </Button>
                      </Space>
                    </Space>
                  ) : (
                    <div className="studio-empty-result">
                      <VideoCameraOutlined />
                      <Text type="secondary">Video hazir oldugunda burada oynatilir</Text>
                    </div>
                  )}
                </Card>

                <Card title="Motion Controls" className="studio-panel-right">
                  <Form layout="vertical" form={videoForm} onFinish={handleVideoSubmit}>
                    <Space direction="vertical" size={14} style={{ width: '100%' }}>
                      <div>
                        <Text strong>Preset Hareket</Text>
                        <div className="studio-segment-list">
                          {MOTION_PRESETS.map((preset) => (
                            <button
                              key={preset.key}
                              type="button"
                              className={`studio-segment-row ${motionPreset === preset.key ? 'selected' : ''}`}
                              onClick={() => setMotionPreset(preset.key)}
                            >
                              <span>{preset.label}</span>
                              <Text type="secondary">{preset.hint}</Text>
                              {motionPreset === preset.key && <CheckOutlined />}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Form.Item name="prompt" label="Custom Prompt (Opsiyonel)">
                        <TextArea rows={3} placeholder="Ek hareket veya kamera niyeti..." />
                      </Form.Item>
                      <Form.Item name="negativePrompt" label="Negative Prompt (Opsiyonel)">
                        <TextArea rows={2} placeholder="Istenmeyen hareket/efekt..." />
                      </Form.Item>

                      <Alert
                        type="info"
                        showIcon
                        message="Varsayilan ciktı"
                        description="qualityPreset: balanced, 480x832 (vertical fashion video)"
                      />

                      {renderVideoStatus()}

                      <Button
                        type="primary"
                        size="large"
                        icon={videoSubmitting ? <LoadingOutlined /> : <VideoCameraOutlined />}
                        loading={videoSubmitting}
                        onClick={() => videoForm.submit()}
                      >
                        Video Olustur
                      </Button>
                    </Space>
                  </Form>
                </Card>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={previewModal.open}
        title={previewModal.title || 'Preview'}
        onCancel={() => setPreviewModal({ open: false })}
        width={960}
        footer={[
          <Button key="close" onClick={() => setPreviewModal({ open: false })}>Kapat</Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadingPreview}
            onClick={handleDownloadPreview}
          >
            Indir
          </Button>,
        ]}
      >
        {previewModal.url && <img src={previewModal.url} alt={previewModal.title || 'Preview'} className="studio-modal-image" />}
      </Modal>
    </div>
  );
};

export default ProductStudioPage;
